import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, ArrowLeft, ShoppingCart, ChefHat, ChevronLeft, ChevronRight, X, ZoomIn, Share2, Link2, Download, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import SpicinessBadge from '../components/SpicinessBadge';
import SafeHtml from '../components/SafeHtml';
import { fetchProductBySlug, fetchProducts } from '../data/products';
import { useCartStore } from '../store/cartStore';
import { useToastStore } from '../store/toastStore';
import { formatPrice, getReferralCode } from '../lib/utils';
import { decodeShareId, buildProductShareUrl } from '../lib/shareCode';
import { trackProductView, trackAddToCart, trackEvent, flushEvents } from '../lib/analytics';
import type { Product } from '../types';

export default function ProductDetail() {
  // 兩條路由共用本頁：/products/:slug 與 /p/:code（3 位分享短碼）
  const { slug: slugParam, id: idParam } = useParams<{ slug?: string; id?: string }>();
  const pageSlug = slugParam || idParam || '';
  const [product, setProduct] = useState<Product | null>(null);
  const [related, setRelated] = useState<Product[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [docDownloading, setDocDownloading] = useState(false);

  // 文件下載：blob 強制儲存——唔信瀏覽器對 <a download> 嘅處理（WeChat X5 等會儲成
  // HTML 殼），fetch 攞真 bytes 再由程式觸發另存，任何瀏覽器都係原檔（2026-10-20）
  const handleDocDownload = async (docUrl: string, docName?: string) => {
    if (!docUrl || docDownloading) return;
    setDocDownloading(true);
    try {
      const res = await fetch(docUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = (docUrl.match(/\.([a-zA-Z0-9]+)(?:\?|$)/) || [])[1] || 'pdf';
      a.download = `${(docName || '下載文件').replace(/[\\/:*?"<>|]/g, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      // fallback：直接新視窗開原檔（伺服器有 attachment disposition）
      window.open(docUrl, '_blank', 'noopener');
    } finally {
      setDocDownloading(false);
    }
  };

  const addItem = useCartStore((s) => s.addItem);
  const addToast = useToastStore((s) => s.addToast);

  useEffect(() => {
    if (!pageSlug) return;
    setLoading(true);
    // /p/:code 短碼（或舊有純數字 id）直接查；否則走 slug 名稱比對
    if (/^\d+$/.test(pageSlug)) {
      loadProductById(Number(pageSlug));
      return;
    }
    const decodedId = decodeShareId(pageSlug);
    if (decodedId) {
      loadProductById(decodedId);
      return;
    }
    fetchProductBySlug(decodeURIComponent(pageSlug)).then((p) => {
      setProduct(p);
      setSelectedImage(0);
      if (p) {
        trackProductView(p.id, p.sku, p.name);
        fetchProducts().then((all) => {
          setRelated(all.filter((x) => x.id !== p.id).slice(0, 3));
        });
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSlug]);

  const loadProductById = (id: number) => {
    fetch(`/api/public/products/${id}`)
      .then((r) => r.json())
      .then((d) => {
        const p = (d?.data && typeof d.data === 'object' ? d.data : null) as Product | null;
        setProduct(p || null);
        setSelectedImage(0);
        if (p) {
          trackProductView(p.id, p.sku, p.name);
          fetchProducts().then((all) => setRelated(all.filter((x) => x.id !== p.id).slice(0, 3)));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleAddToCart = () => {
    if (!product) return;
    addItem(product, quantity);
    const effPrice = product.isSecondhand === 1 && product.secondhandPrice != null && product.secondhandPrice > 0 ? product.secondhandPrice : product.price;
    trackAddToCart(product.id, quantity, effPrice);
    addToast(`已將 ${quantity} 件「${product.name}」加入購物車`, 'success');
  };

  // 開心分享：/p/{3位短碼} 短連結；點「分享」才展開社交選單，主畫面保持乾淨
  // 轉介歸因鏈：訪客經 ?r= 到達後分享產品，連結自動帶同一轉介碼，
  // 朋友打開即被歸因給原轉介人（落單佣金照計），鼓勵轉介人積極分享。
  const rawShareUrl = product ? buildProductShareUrl(window.location.origin, product.id) : '';
  const refCode = getReferralCode();
  const shareUrl = rawShareUrl && refCode ? `${rawShareUrl}?r=${refCode}` : rawShareUrl;
  const shareText = product ? `🔥 ${product.name}` : '';

  const trackShare = (method: string) => {
    if (!product) return;
    trackEvent('product_share', { productId: product.id, method });
    flushEvents();
  };

  const copyShareUrl = async (): Promise<boolean> => {
    if (!shareUrl) return false;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    return true;
  };

  // IG / 小紅書冇網頁分享 intent：複製連結再帶用户去平台貼上
  const copyAndOpen = async (platform: string, url: string) => {
    const ok = await copyShareUrl();
    addToast(ok ? `連結已複製，去 ${platform} 貼上啦！` : '複製失敗', ok ? 'success' : 'error');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Lightbox：Esc 關閉、左右方向鍵切換、鎖背景捲動
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const imgs = product ? [product.imageUrl, product.imageUrl2, product.imageUrl3].filter(Boolean) : [];
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (imgs.length > 1) {
        if (e.key === 'ArrowLeft') setSelectedImage((i) => (i === 0 ? imgs.length - 1 : i - 1));
        if (e.key === 'ArrowRight') setSelectedImage((i) => (i === imgs.length - 1 ? 0 : i + 1));
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen, product]);

  const prevImage = () => {
    setSelectedImage((i) => (i === 0 ? productImages.length - 1 : i - 1));
  };

  const nextImage = () => {
    setSelectedImage((i) => (i === productImages.length - 1 ? 0 : i + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">載入中...</div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">找不到產品</h1>
          <Link to="/products" className="btn-primary">返回產品目錄</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const productImages = [product.imageUrl, product.imageUrl2, product.imageUrl3].filter(Boolean) as string[];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/products" className="inline-flex items-center gap-1 text-stone-600 hover:text-x1493-primary mb-6">
            <ArrowLeft size={18} />
            返回產品目錄
          </Link>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-4">
              <div
                className="relative rounded-3xl overflow-hidden bg-stone-100 aspect-square group cursor-zoom-in"
                onClick={() => productImages[selectedImage] && setLightboxOpen(true)}
                title="點擊放大"
              >
                {productImages[selectedImage] ? (
                  <img src={productImages[selectedImage]} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-8xl">🌶️</div>
                )}

                {productImages[selectedImage] && (
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <ZoomIn size={18} />
                  </div>
                )}

                {productImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-stone-700 shadow opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-white"
                      aria-label="上一張"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-stone-700 shadow opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-white"
                      aria-label="下一張"
                    >
                      <ChevronRight size={24} />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {productImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); setSelectedImage(idx); }}
                          className={`w-2.5 h-2.5 rounded-full transition ${
                            idx === selectedImage ? 'bg-x1493-primary' : 'bg-white/80 hover:bg-white'
                          }`}
                          aria-label={`切換到圖片 ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {productImages.length > 1 && (
                <div className="flex gap-3 justify-center">
                  {productImages.map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-20 rounded-xl overflow-hidden bg-stone-100 border-2 transition ${
                        selectedImage === idx ? 'border-x1493-primary' : 'border-transparent hover:border-stone-300'
                      }`}
                    >
                      <img src={url} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <SpicinessBadge level={product.spicinessLevel} size="lg" />
                {product.isSecondhand === 1 && (
                  <span className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-base font-semibold bg-sky-100 text-sky-700">
                    ♻️ 二手
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mt-4 mb-4">{product.name}</h1>
              {product.isSecondhand === 1 && product.secondhandPrice != null && product.secondhandPrice > 0 ? (
                <p className="flex flex-wrap items-baseline gap-3 mb-6">
                  <span className="text-xl text-stone-400 line-through">{formatPrice(product.price)}</span>
                  <span className="text-3xl font-bold text-x1493-primary">{formatPrice(product.secondhandPrice)}</span>
                  <span className="text-base font-bold text-sky-700">二手價</span>
                </p>
              ) : (
                <p className="text-3xl font-bold text-x1493-primary mb-6">
                  {formatPrice(product.price)}
                  {product.originalPrice && product.originalPrice > product.price && (
                    <span className="ml-3 text-lg text-stone-400 line-through">{formatPrice(product.originalPrice)}</span>
                  )}
                </p>
              )}

              <div className="text-stone-600 leading-relaxed mb-8 whitespace-pre-line [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_a]:text-x1493-primary [&_a]:underline">
                <SafeHtml html={product.description || ''} />
              </div>

              {(product.docs && product.docs.length > 0 ? product.docs : product.docUrl ? [{ name: product.docName || '下載文件', url: product.docUrl }] : []).length > 0 && (() => {
                const docs = product.docs && product.docs.length > 0 ? product.docs : [{ name: product.docName || '下載文件', url: product.docUrl! }];
                return (
                  <div className="mb-8">
                    <h3 className="font-bold mb-3 flex items-center gap-2">
                      <FileText size={20} className="text-x1493-secondary" />
                      產品資料
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {docs.map((doc, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleDocDownload(doc.url, doc.name)}
                          disabled={docDownloading}
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-x1493-primary text-x1493-primary font-bold hover:bg-x1493-primary hover:text-white transition disabled:opacity-60"
                        >
                          <Download size={18} className={docDownloading ? 'animate-pulse' : ''} />
                          {docDownloading ? '下載中…' : doc.name || `下載文件 ${idx + 1}`}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {product.suitableDishes && product.suitableDishes.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <ChefHat size={20} className="text-x1493-secondary" />
                    適用場景
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.suitableDishes.map((dish) => (
                      <span key={dish} className="bg-stone-100 text-stone-700 px-3 py-1.5 rounded-lg text-sm font-medium">
                        {dish}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
                <div className="flex items-center gap-4">
                  <span className="font-bold text-stone-700">數量</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-stone-200"
                    >
                      <Minus size={18} />
                    </button>
                    <span className="w-10 text-center font-bold text-lg">{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-stone-200"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>

                {/* 開心分享 —— 緊湊按鈕 + 品牌 logo 選單 */}
                <div className="relative">
                  {shareOpen && (
                    <div className="fixed inset-0 z-30" onClick={() => setShareOpen(false)} aria-hidden="true" />
                  )}
                  <button
                    type="button"
                    onClick={() => setShareOpen((v) => !v)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border font-bold text-sm transition ${
                      shareOpen
                        ? 'border-x1493-primary bg-red-50 dark:bg-red-900/20 text-x1493-primary'
                        : 'border-stone-300 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary'
                    }`}
                  >
                    <Share2 size={16} />
                    開心分享
                  </button>
                  {shareOpen && (
                    <div className="absolute right-0 top-full mt-2 z-40 w-52 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-stone-200 dark:border-gray-700 overflow-hidden py-1">
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => { trackShare('whatsapp'); setShareOpen(false); }}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-gray-200 hover:bg-stone-100 dark:hover:bg-gray-700"
                      >
                        <img src="https://cdn.simpleicons.org/whatsapp/25D366" alt="" className="w-5 h-5" />
                        WhatsApp
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => { trackShare('facebook'); setShareOpen(false); }}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-gray-200 hover:bg-stone-100 dark:hover:bg-gray-700"
                      >
                        <img src="https://cdn.simpleicons.org/facebook/1877F2" alt="" className="w-5 h-5" />
                        Facebook
                      </a>
                      <button
                        type="button"
                        onClick={() => { trackShare('instagram'); setShareOpen(false); copyAndOpen('IG', 'https://www.instagram.com/'); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-gray-200 hover:bg-stone-100 dark:hover:bg-gray-700"
                      >
                        <img src="https://cdn.simpleicons.org/instagram/E4405F" alt="" className="w-5 h-5" />
                        IG
                      </button>
                      <button
                        type="button"
                        onClick={() => { trackShare('xiaohongshu'); setShareOpen(false); copyAndOpen('小紅書', 'https://www.xiaohongshu.com/'); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-gray-200 hover:bg-stone-100 dark:hover:bg-gray-700"
                      >
                        <img src="https://cdn.simpleicons.org/xiaohongshu/FF2442" alt="" className="w-5 h-5" />
                        小紅書
                      </button>
                      <button
                        type="button"
                        onClick={() => { copyShareUrl().then((ok) => addToast(ok ? '已複製分享連結' : '複製失敗', ok ? 'success' : 'error')); trackShare('copy'); setShareOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-gray-200 hover:bg-stone-100 dark:hover:bg-gray-700 border-t border-stone-100 dark:border-gray-700"
                      >
                        <Link2 size={18} className="text-stone-500" />
                        複製連結
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={product.stockQuantity <= 0}
                className="w-full md:w-auto btn-primary flex items-center justify-center gap-2"
              >
                <ShoppingCart size={20} />
                加入購物車
              </button>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold mb-6">你可能也會喜歡</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((p) => (
                  <ProductCard key={p.id} product={p} compact />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 圖片放大 Lightbox */}
      {lightboxOpen && productImages[selectedImage] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="放大圖片"
        >
          <button
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            aria-label="關閉"
          >
            <X size={24} />
          </button>

          {productImages.length > 1 && (
            <>
              <button
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                aria-label="上一張"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                aria-label="下一張"
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}

          <img
            src={productImages[selectedImage]}
            alt={product.name}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {productImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
              {selectedImage + 1} / {productImages.length}
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}
